'use client';

import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useMemo } from 'react';

import { useViewItemListImpressions } from '@libs/analytics';
import { type FrefListCodes } from '@libs/common-next';
import { type BuyItAgainItem } from '@libs/common-types';

import { CarouselProductCard } from './components';
import { mapBuyItAgainViewItems, type ViewItemListAttribution } from './hooks';

import './buy-it-again-carousel.scss';

const MAX_VISIBLE_PRODUCTS = 6;

const HOME_FREF_ATTRIBUTION: FrefListCodes = { listId: 'splp', listName: 'bia' };

type BuyItAgainCarouselProps = {
    readonly header: string;
    readonly subheader?: string;
    readonly products: BuyItAgainItem[] | undefined;
    readonly minCount: number;
    readonly className?: string;
    readonly excludes?: readonly string[];
    readonly frefAttribution?: FrefListCodes;
    /**
     * Opts the carousel into batched `view_item_list` impressions (e.g. Cart BIA).
     * When omitted, each card keeps the standalone Home Page impression event.
     */
    readonly viewItemList?: ViewItemListAttribution;
};

export function BuyItAgainCarousel({
    header,
    subheader,
    products,
    minCount,
    className,
    excludes = [],
    frefAttribution = HOME_FREF_ATTRIBUTION,
    viewItemList,
}: BuyItAgainCarouselProps) {
    const excludedSkus = new Set(excludes);
    const eligibleProducts = products?.filter(product => !excludedSkus.has(product.sku));
    const visibleProducts = (eligibleProducts ?? []).slice(0, MAX_VISIBLE_PRODUCTS);

    const impressionItems = useMemo(
        () => (viewItemList ? mapBuyItAgainViewItems(visibleProducts, viewItemList) : []),
        [viewItemList, visibleProducts]
    );
    const { onVisible } = useViewItemListImpressions({ items: impressionItems });

    if (!eligibleProducts || eligibleProducts.length < minCount) return null;

    return (
        <LayoutContainer className={cn('buy-it-again-carousel', className)}>
            <Section align="center" headingTagName="h2" title={header} description={subheader}>
                <ul className="buy-it-again-carousel__products" aria-label={header}>
                    {visibleProducts.map((product, index) => (
                        <li key={product.sku}>
                            <CarouselProductCard
                                recommendation={product}
                                position={index + 1}
                                frefAttribution={frefAttribution}
                                onView={viewItemList ? () => onVisible(index) : undefined}
                            />
                        </li>
                    ))}
                </ul>
            </Section>
        </LayoutContainer>
    );
}
